import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CompteCaisse, Reglement } from '../models/models'; // assuming you put your Reglement interface there
import api from './api';


export interface ReglementFilter {
  owner_id: number;
  limit: number;
  offset: number;
  type_reglement: 'ENCAISSEMENT' | 'PAIEMENT' | 'MOUVEMENT' | 'OPERATION_DIVERSE';
  sens: 'C' | 'D' | '';
  tiers_type?: string | null;
  caisse_id?: number;
  tiers_id?: number | null;
  nature_id?: number | null;
  categorie_nature_id?: number | null;
  mode_paiement_id?: number | null;
  date_start?: string;
  date_end?: string;
  montant?: number | null;
  departement_id?: number | null;
  departement?: any;
  tiers: any;
  caisse: any;
  nature?: any;
  categorie_nature?: any;
  mode_paiement?: any;
}

interface ReglementState {
  reglementData: Reglement[];
  compteCaisse: CompteCaisse[];
  total: number;
  totalPages: number;
  currentPage: number;
  totalCredit: number;   // Σ montants crédit sur tout le jeu filtré (hors pagination)
  totalDebit: number;    // Σ montants débit sur tout le jeu filtré (hors pagination)
  soldeCaisse: number | null; // solde courant de la caisse filtrée (null si aucune caisse)
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedReglement: Reglement | null;
  filters: ReglementFilter;
  
}

const initialState: ReglementState = {
  reglementData: [],
  compteCaisse: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  totalCredit: 0,
  totalDebit: 0,
  soldeCaisse: null,
  loading: false,
  error: null,
  successMessage: null,
  selectedReglement: null,
  filters: {
    owner_id: 1,
    limit: 5,
    offset: 0,
    type_reglement: 'ENCAISSEMENT',
    sens: 'C',
    caisse_id: undefined,
    tiers_id: undefined,
    date_start: undefined,
    date_end: undefined,
    tiers: null,
    caisse: null,
  },
  
};




// Fetch all Reglements
export const fetchReglementData = createAsyncThunk<
  { data: Reglement[]; total: number; totalPages: number; currentPage: number; totalCredit?: number; totalDebit?: number; soldeCaisse?: number | null },
  {
    owner_id: number;
    limit: number;
    offset: number;
    type_reglement: 'ENCAISSEMENT' | 'PAIEMENT' | 'MOUVEMENT' | 'OPERATION_DIVERSE';
    sens: 'C' | 'D' | '';
    tiers_type?: string | null;
    caisse_id?: number;
    tiers_id?: number|null;
    nature_id?: number | null;
    categorie_nature_id?: number | null;
    mode_paiement_id?: number | null;
    date_start?: string;
    date_end?: string;
    montant?: number | null;
    departement_id?: number | null;
  },
  { rejectValue: string }
>(
  'reglement/fetchReglementData',
  async (
    { owner_id, limit, offset, type_reglement, sens, tiers_type, caisse_id, tiers_id, nature_id, categorie_nature_id, mode_paiement_id, date_start, date_end, montant, departement_id },
    thunkAPI
  ) => {
    try {
      let endpoint = '';
      if (type_reglement === 'ENCAISSEMENT') {
        endpoint = '/reglements/encaissement';
      } else if (type_reglement === 'PAIEMENT') {
        endpoint ='/reglements/paiement';
      } else if (type_reglement === 'MOUVEMENT') {
        endpoint ='/reglements/mouvement';
      } else if (type_reglement === 'OPERATION_DIVERSE') {
        endpoint ='/reglements/operationDiverse';
      }

      const params: Record<string, any> = {
        owner_id,
        limit,
        offset,
      };

      if (caisse_id !== undefined) params.caisse_id = caisse_id;
      if (tiers_id !== undefined) params.tiers_id = tiers_id;
      if (tiers_type) params.tiers_type = tiers_type;
      if (nature_id != null) params.nature_id = nature_id;
      if (categorie_nature_id != null) params.categorie_nature_id = categorie_nature_id;
      if (mode_paiement_id != null) params.mode_paiement_id = mode_paiement_id;
      // sens (C/D) pour mouvement de caisse ET opération diverse ; '' = tous → non envoyé
      if ((type_reglement === 'MOUVEMENT' || type_reglement === 'OPERATION_DIVERSE') && (sens === 'C' || sens === 'D')) params.sens = sens;
      if (date_start) params.date_start = date_start;
      if (date_end) params.date_end = date_end;
      if (montant != null) params.montant = montant;
      if (departement_id != null) params.departement_id = departement_id;

      const response = await api.get(endpoint, { params });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

export const fetchEtatCaisse = createAsyncThunk<
  { data: CompteCaisse[]; total: number; totalPages: number; currentPage: number },
  {
    owner_id: number;
    caisse_id?: number;
    startDate?: string;
    endDate?: string;
    limit: number;
    offset: number;
  },
  { rejectValue: string }
>(
  'reglement/fetchetatCaisse',
  async (
    { owner_id, limit, offset, caisse_id, startDate, endDate },
    thunkAPI
  ) => {
    try {
      let endpoint = '/reglements/etatCaisse';
    
      const params: Record<string, any> = {
        owner_id,
        limit,
        offset,
      };

      if (caisse_id !== undefined) params.caisse_id = caisse_id;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get(endpoint, { params });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

// Create or update a Reglement
export const createReglement = createAsyncThunk<
  Reglement,
  Reglement,
  { rejectValue: string }
>(
  'reglement/createOrUpdateReglement',
  async (reglementData, thunkAPI) => {
    try {
      const response = await api.post('/reglements/createOrUpdate', reglementData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
    }
  }
);

// Transfert équilibré entre deux comptes tiers (2 OD de sens opposés)
export const createTransfert = createAsyncThunk<
  { legA: Reglement | null; legB: Reglement | null },
  {
    tiers_a_id: number;
    tiers_b_id: number;
    sens_a: 'C' | 'D';
    montant: number;
    reference?: string | null;
    date_reglement: string;
    departement_id: number;
  },
  { rejectValue: string }
>(
  'reglement/createTransfert',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post('/reglements/transfert', payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create transfert.');
    }
  }
);

// Delete a Reglement
// id = clé du règlement visé (numero_reglement n'est pas unique entre départements)
export const deleteReglement = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'reglement/deleteReglement',
  async (id, thunkAPI) => {
    try {
      await api.delete('/reglements', { params: { id } });
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
    }
  }
);

const reglementSlice = createSlice({
  name: 'reglement',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedReglement: (state, action: PayloadAction<Reglement | null>) => {
      state.selectedReglement = action.payload;
    },
    clearReglementData: (state) => {
      state.reglementData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setFilters: (state, action: PayloadAction<Partial<ReglementFilter>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReglementData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReglementData.fulfilled, (state, action) => {
        state.loading = false;
        state.reglementData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.totalCredit = action.payload.totalCredit ?? 0;
        state.totalDebit = action.payload.totalDebit ?? 0;
        state.soldeCaisse = action.payload.soldeCaisse ?? null;
      })
      .addCase(fetchReglementData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
       .addCase(fetchEtatCaisse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEtatCaisse.fulfilled, (state, action) => {
        state.loading = false;
        state.compteCaisse = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchEtatCaisse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createReglement.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createReglement.fulfilled, (state, action: PayloadAction<Reglement>) => {
        state.loading = false;
        state.successMessage = 'Creation/Update successful!';
      })
      .addCase(createReglement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTransfert.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createTransfert.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Transfert enregistré !';
      })
      .addCase(createTransfert.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteReglement.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteReglement.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Deletion successful!';
        state.reglementData = state.reglementData.filter((r) => r.id !== action.payload);
      })
      .addCase(deleteReglement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentPage, setSelectedReglement, clearReglementData, clearMessages ,setFilters} = reglementSlice.actions;

export default reglementSlice.reducer;
